import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import api from '../../../services/api';
import AICoach from '../../../components/ai/AICoach';
import MyPracticeResults from '../../../components/microsim/MyPracticeResults';
import copyToClipboard from '../../../utils/clipboard';
import logoFull from '../../../assets/images/logo_aktivity.png';
import logoIcon from '../../../assets/images/standalone_logo.png';
import { formatLocalDate, formatInstantDate } from '../../../utils/dateUtils';

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'bg-neutral-100 text-neutral-700', icon: '⬜' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: '🔄' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: '✅' },
  blocked: { label: 'Blocked', color: 'bg-red-100 text-red-700', icon: '🔴' },
};

const PostSessionTab = ({ sessionId, participantId, onBack, embedded = false, activeSection }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [noteText, setNoteText] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeReflection, setActiveReflection] = useState(null);
  const [reflectionAnswers, setReflectionAnswers] = useState({});
  const [activeNavItem, setActiveNavItem] = useState(activeSection || 'welcome');

  // Sync activeSection from parent (lobby) when it changes
  useEffect(() => {
    if (activeSection) setActiveNavItem(activeSection);
  }, [activeSection]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => { fetchPostSession(); }, [sessionId]);

  const fetchPostSession = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/participant/sessions/${sessionId}/post-session`);
      if (response.success) setData(response.data);
    } catch (error) {
      console.error('Error fetching post-session:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (planIndex, newStatus) => {
    setSaving(true);
    try {
      await api.put(`/participant/sessions/${sessionId}/post-session/action-plans/${planIndex}/status`, { status: newStatus });
      toast.success('Status updated!');
      fetchPostSession();
    } catch (error) { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleAddNote = async (planIndex) => {
    const text = noteText[planIndex]?.trim();
    if (!text) return;
    setSaving(true);
    try {
      await api.post(`/participant/sessions/${sessionId}/post-session/action-plans/${planIndex}/notes`, { text });
      toast.success('Note added!');
      setNoteText(prev => ({ ...prev, [planIndex]: '' }));
      fetchPostSession();
    } catch (error) { toast.error('Failed to add note'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-neutral-500 mb-4">Post-session data not available yet</p>
          {onBack && <button onClick={onBack} className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm">Go Back</button>}
        </div>
      </div>
    );
  }

  const { participant, actionPlans, summary, config, reflections } = data;

  // The cycle is configurable per session (Rashmi used 7/15/30), so read it from
  // the entries rather than repeating the old 30/60/90 default as fixed copy.
  const reflectionIntervalLabel = (() => {
    const days = (reflections || []).map((r) => Number(r.intervalDays)).filter(Number.isFinite);
    if (!days.length) return '';
    const sorted = [...new Set(days)].sort((a, b) => a - b);
    if (sorted.length === 1) return `${sorted[0]} days`;
    return `${sorted.slice(0, -1).join(', ')}, and ${sorted[sorted.length - 1]} days`;
  })();

  // Build sidebar nav items
  const navigationItems = [
    {
      id: 'welcome',
      name: 'Overview',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
        </svg>
      ),
      onClick: () => setActiveNavItem('welcome'),
    },
    {
      id: 'ai-coach',
      // Short label to keep the sidebar tidy — the full "Aktivity Intelligence™ (AI) Coach"
      // branding is used in the page header and the screen body below.
      name: 'AI Coach',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
        </svg>
      ),
      onClick: () => setActiveNavItem('ai-coach'),
    },
    {
      id: 'action-plans',
      name: 'Action Plans',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      onClick: () => setActiveNavItem('action-plans'),
      badge: actionPlans?.length > 0 ? `${summary.completed}/${summary.totalActionPlans}` : null,
    },
    {
      id: 'reflections',
      name: 'Reflections',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      onClick: () => setActiveNavItem('reflections'),
    },
  ];

  const bottomNavItems = [];

  const getPageTitle = () => {
    const item = navigationItems.find(n => n.id === activeNavItem);
    return item?.name || 'Post-Session';
  };

  // ===== SECTION RENDERS =====

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-neutral-200 p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
            <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-neutral-900">Post-Session Tracking</h1>
            <p className="text-neutral-500 text-sm">Track your action plans and reflect on your progress</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <div className="bg-white rounded-lg sm:rounded-xl border border-neutral-200 p-2.5 sm:p-4 text-center">
          <p className="text-2xl font-bold text-neutral-900">{summary.totalActionPlans}</p>
          <p className="text-xs text-neutral-500">Total Plans</p>
        </div>
        <div className="bg-white rounded-lg sm:rounded-xl border border-green-200 p-2.5 sm:p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{summary.completed}</p>
          <p className="text-xs text-neutral-500">Completed</p>
        </div>
        <div className="bg-white rounded-lg sm:rounded-xl border border-blue-200 p-2.5 sm:p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{summary.inProgress}</p>
          <p className="text-xs text-neutral-500">In Progress</p>
        </div>
        <div className="bg-white rounded-lg sm:rounded-xl border border-red-200 p-2.5 sm:p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{summary.blocked}</p>
          <p className="text-xs text-neutral-500">Blocked</p>
        </div>
      </div>

      {/* Manager Code */}
      {config.managerReinforcement && participant.managerCode && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
              </div>
              <div>
                <p className="text-xs text-purple-600 font-medium uppercase tracking-wider">Manager Code</p>
                <p className="text-xl font-mono font-bold text-purple-700">{participant.managerCode}</p>
              </div>
            </div>
            <button onClick={async () => {
                const ok = await copyToClipboard(participant.managerCode);
                if (ok) toast.success('Copied!');
                else toast.error('Could not copy — long-press the code to copy manually.');
              }}
              className="px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors">Copy</button>
          </div>
          <p className="text-xs text-purple-500 mt-2">Share this code with your manager to track your progress</p>
        </div>
      )}

      {/* Micro simulation results (renders nothing if none) */}
      <MyPracticeResults sessionId={sessionId} />

      {/* Quick Links */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <button onClick={() => setActiveNavItem('action-plans')}
          className="bg-white rounded-xl border border-neutral-200 p-6 text-left hover:border-primary-300 hover:shadow-md transition-all group">
          <h3 className="text-base font-bold text-neutral-900 mb-1">Action Plans</h3>
          <p className="text-sm text-neutral-500">Update your progress on {summary.totalActionPlans} action plans</p>
          <div className="mt-3 flex items-center gap-2 text-primary-600 text-sm font-medium">
            Open <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>
          </div>
        </button>
        <button onClick={() => setActiveNavItem('reflections')}
          className="bg-white rounded-xl border border-neutral-200 p-6 text-left hover:border-primary-300 hover:shadow-md transition-all group">
          <h3 className="text-base font-bold text-neutral-900 mb-1">Reflections</h3>
          <p className="text-sm text-neutral-500">{reflectionIntervalLabel ? `Reflect on your progress at ${reflectionIntervalLabel}` : 'Reflect on your progress after the session'}</p>
          <div className="mt-3 flex items-center gap-2 text-primary-600 text-sm font-medium">
            Open <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>
          </div>
        </button>
      </div>
    </div>
  );

  const renderActionPlans = () => {
    const completionPercent = summary.totalActionPlans > 0 ? Math.round((summary.completed / summary.totalActionPlans) * 100) : 0;
    return (
      <div className="space-y-6">
        {/* Progress Bar */}
        <div className="bg-white rounded-xl border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-neutral-900">Your Progress</h2>
            <span className="text-2xl font-bold text-primary-600">{completionPercent}%</span>
          </div>
          <div className="w-full bg-neutral-100 rounded-full h-3 mb-3">
            <motion.div className="bg-gradient-to-r from-primary-500 to-green-500 h-3 rounded-full"
              initial={{ width: 0 }} animate={{ width: `${completionPercent}%` }} transition={{ duration: 0.8, ease: 'easeOut' }} />
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="p-2 rounded-lg bg-neutral-50"><p className="text-lg font-bold">{summary.totalActionPlans}</p><p className="text-[10px] text-neutral-500">Total</p></div>
            <div className="p-2 rounded-lg bg-green-50"><p className="text-lg font-bold text-green-600">{summary.completed}</p><p className="text-[10px] text-green-600">Done</p></div>
            <div className="p-2 rounded-lg bg-blue-50"><p className="text-lg font-bold text-blue-600">{summary.inProgress}</p><p className="text-[10px] text-blue-600">Active</p></div>
            <div className="p-2 rounded-lg bg-red-50"><p className="text-lg font-bold text-red-600">{summary.blocked}</p><p className="text-[10px] text-red-600">Blocked</p></div>
          </div>
        </div>

        {/* Action Plan Cards */}
        {actionPlans.length === 0 ? (
          <div className="bg-white rounded-xl p-10 border text-center">
            <p className="text-lg font-medium text-neutral-500">No action plans yet</p>
            <p className="text-sm text-neutral-400 mt-1">Action plans from your session will appear here</p>
          </div>
        ) : (
          <div className="space-y-5">
            {actionPlans.map((plan, idx) => {
              const statusConfig = STATUS_CONFIG[plan.status] || STATUS_CONFIG.not_started;
              return (
                <div key={idx} className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                  <div className={`h-1.5 ${plan.status === 'completed' ? 'bg-green-500' : plan.status === 'in_progress' ? 'bg-blue-500' : plan.status === 'blocked' ? 'bg-red-500' : 'bg-neutral-200'}`}></div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <h3 className="text-lg font-bold text-neutral-900">{plan.title || `Action Plan ${idx + 1}`}</h3>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${statusConfig.color}`}>{statusConfig.icon} {statusConfig.label}</span>
                    </div>

                    {(plan.byWhen || plan.commitment != null) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        {plan.byWhen && (
                          <div className="flex items-center gap-2 p-2.5 bg-blue-50 rounded-lg text-sm">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            <span className="text-neutral-700">Timeline: <strong>{plan.byWhen.replace(/_/g, ' ')}</strong></span>
                          </div>
                        )}
                        {plan.commitment != null && (
                          <div className="flex items-center gap-2 p-2.5 bg-amber-50 rounded-lg text-sm">
                            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" /></svg>
                            <span className="text-neutral-700">Commitment: <strong>{plan.commitment}/10</strong></span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Status Buttons */}
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-neutral-500 mb-2 uppercase tracking-wider">Update Status</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                          <button key={key} onClick={() => handleStatusChange(idx, key)} disabled={saving}
                            className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border-2 transition-all text-sm ${
                              plan.status === key ? `${cfg.color} border-current shadow-sm` : 'bg-white border-neutral-200 text-neutral-500 hover:border-neutral-300'
                            }`}>
                            <span className="text-xl">{cfg.icon}</span>
                            <span className="font-medium text-xs">{cfg.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {plan.managerValidated && (
                      <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg mb-4 border border-green-100">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                        <span className="text-sm font-medium text-green-800">Validated by Manager</span>
                      </div>
                    )}

                    {/* Notes */}
                    {plan.progressNotes?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-neutral-500 mb-2 uppercase tracking-wider">Notes ({plan.progressNotes.length})</p>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {plan.progressNotes.map((note, nIdx) => (
                            <div key={nIdx} className={`p-3 rounded-lg text-sm ${note.author === 'manager' ? 'bg-purple-50 border border-purple-100' : 'bg-neutral-50 border border-neutral-100'}`}>
                              <div className="flex justify-between items-center mb-1">
                                <span className="font-medium text-xs">{note.authorName || (note.author === 'manager' ? 'Manager' : 'You')}</span>
                                <span className="text-xs text-neutral-400">{new Date(note.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                              </div>
                              <p className="text-neutral-700">{note.text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add Note */}
                    <div className="flex gap-2">
                      <input type="text" value={noteText[idx] || ''}
                        onChange={(e) => setNoteText(prev => ({ ...prev, [idx]: e.target.value }))}
                        placeholder="Add a progress note..."
                        className="flex-1 px-3 py-2.5 border border-neutral-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddNote(idx)} />
                      <button onClick={() => handleAddNote(idx)} disabled={saving || !noteText[idx]?.trim()}
                        className="px-4 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 disabled:opacity-30">Add</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderReflections = () => (
    <div className="space-y-6">
      {config.reflectionCycles && reflections?.length > 0 ? (
        <>
          <div>
            <h1 className="text-xl font-bold text-neutral-900">My Reflections</h1>
            <p className="text-sm text-neutral-500 mt-1">{reflectionIntervalLabel ? `Reflect on your progress at ${reflectionIntervalLabel}` : 'Reflect on your progress after the session'}</p>
          </div>
          <div className="space-y-3">
            {reflections.map((reflection, idx) => (
              <div key={idx} className="bg-white rounded-xl p-4 border border-neutral-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-neutral-900">{reflection.intervalDays}-Day Reflection</h3>
                    <p className="text-xs text-neutral-500">
                      {reflection.status === 'available' ? 'Ready to complete' :
                       reflection.status === 'completed' ? `Completed ${formatInstantDate(reflection.submittedAt)}` :
                       reflection.status === 'not_available' ? `Available ${formatInstantDate(reflection.availableAt)}` : 'Missed'}
                    </p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    reflection.status === 'completed' ? 'bg-green-100 text-green-700' :
                    reflection.status === 'available' ? 'bg-blue-100 text-blue-700' :
                    reflection.status === 'missed' ? 'bg-red-100 text-red-700' : 'bg-neutral-100 text-neutral-500'
                  }`}>{reflection.status.replace('_', ' ')}</span>
                </div>

                {reflection.status === 'completed' && reflection.responses?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {reflection.responses.map((r, rIdx) => (
                      <div key={rIdx} className="p-2 bg-green-50 rounded-lg">
                        <p className="text-xs font-medium text-neutral-700">{r.questionText}</p>
                        <p className="text-sm text-neutral-900 mt-1">{r.answer}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Missed reflection — explain why and point to the next one.
                    Without this the participant just sees "Missed" with no
                    context, which feels punitive. Showing the next upcoming
                    or open reflection makes it clear that future check-ins
                    are still live. */}
                {reflection.status === 'missed' && (() => {
                  // "Next" = first non-missed reflection AFTER this one that
                  // the participant can still act on (available now, or
                  // arriving in the future). Sort defensively by interval
                  // in case the array isn't already ordered.
                  const sorted = [...reflections].sort(
                    (a, b) => Number(a.intervalDays) - Number(b.intervalDays)
                  );
                  const next = sorted.find(r =>
                    Number(r.intervalDays) > Number(reflection.intervalDays) &&
                    (r.status === 'available' || r.status === 'not_available')
                  );
                  return (
                    <div className="mt-3 rounded-lg bg-red-50 ring-1 ring-inset ring-red-100 px-3 py-2.5 text-xs text-red-800">
                      <p className="font-medium">This reflection's window closed.</p>
                      <p className="mt-1 text-red-700">
                        {next
                          ? next.status === 'available'
                            ? <>Your {next.intervalDays}-day reflection is ready now — complete it below.</>
                            : <>Your next reflection ({next.intervalDays}-day) opens on {formatInstantDate(next.availableAt)}.</>
                          : <>No further reflections are scheduled for this session.</>}
                      </p>
                    </div>
                  );
                })()}

                {reflection.status === 'available' && (
                  activeReflection === reflection.intervalDays ? (
                    <div className="mt-3 space-y-3">
                      {(data.session?.reflectionQuestions || [
                        { questionText: 'What action did you implement?' },
                        { questionText: 'What result did you observe?' },
                        { questionText: 'What challenge remains?' },
                        { questionText: 'What support do you need?' },
                      ]).map((q, qIdx) => (
                        <div key={qIdx}>
                          <label className="text-sm font-medium text-neutral-700">{q.questionText}</label>
                          <textarea value={reflectionAnswers[`${reflection.intervalDays}-${qIdx}`] || ''}
                            onChange={(e) => setReflectionAnswers(prev => ({ ...prev, [`${reflection.intervalDays}-${qIdx}`]: e.target.value }))}
                            className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-300" rows={2} placeholder="Your reflection..." />
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <button onClick={() => setActiveReflection(null)} className="px-4 py-2 text-sm text-neutral-600 border rounded-lg hover:bg-neutral-50">Cancel</button>
                        <button disabled={saving} onClick={async () => {
                          setSaving(true);
                          try {
                            const questions = data.session?.reflectionQuestions || [
                              { questionText: 'What action did you implement?' }, { questionText: 'What result did you observe?' },
                              { questionText: 'What challenge remains?' }, { questionText: 'What support do you need?' },
                            ];
                            const responses = questions.map((q, qIdx) => ({ questionText: q.questionText, answer: reflectionAnswers[`${reflection.intervalDays}-${qIdx}`] || '' }));
                            await api.post(`/participant/sessions/${sessionId}/post-session/reflections/${reflection.intervalDays}`, { responses });
                            toast.success('Reflection submitted!');
                            setActiveReflection(null); setReflectionAnswers({}); fetchPostSession();
                          } catch (error) { toast.error('Failed to submit'); } finally { setSaving(false); }
                        }} className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50">
                          {saving ? 'Submitting...' : 'Submit Reflection'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setActiveReflection(reflection.intervalDays)}
                      className="mt-3 w-full py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600">Complete Reflection</button>
                  )
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <p className="text-sm text-neutral-500">Reflection cycles are not enabled for this session</p>
        </div>
      )}
    </div>
  );

  const renderAICoach = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-neutral-900">Aktivity Intelligence™ (AI) Coach</h1>
        <p className="text-sm text-neutral-500 mt-1">Get personalized coaching based on your training and action plans</p>
      </div>
      <AICoach sessionId={sessionId} participantId={participantId} />
    </div>
  );

  const renderContent = () => {
    switch (activeNavItem) {
      case 'welcome': return renderOverview();
      case 'action-plans': return renderActionPlans();
      case 'reflections': return renderReflections();
      case 'ai-coach': return renderAICoach();
      default: return renderOverview();
    }
  };

  // ==================== EMBEDDED MODE (inside lobby page - no sidebar/header) ====================
  if (embedded) {
    return (
      <div className="p-4 sm:p-6 bg-white">
        {renderContent()}
      </div>
    );
  }

  // ==================== LAYOUT ====================
  return (
    <div className="min-h-screen bg-white flex">
      {/* Desktop Sidebar */}
      <motion.aside initial={false} animate={{ width: sidebarCollapsed ? 80 : 256 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="hidden lg:flex flex-col bg-white border-r border-neutral-200 h-screen sticky top-0">
        {/* Logo */}
        <div className="p-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.img src={logoFull} alt="Aktivity" className="h-8 object-contain"
                  initial={{ opacity: 0, width: 0 }} animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }} transition={{ duration: 0.2 }} />
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Back Button */}
        {onBack && (
          <div className="px-3 pt-2">
            <button onClick={onBack}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-all text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {!sidebarCollapsed && <span>Back</span>}
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 px-3">
          <ul className="space-y-1">
            {navigationItems.map((item) => (
              <li key={item.id}>
                <button onClick={item.onClick}
                  className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    activeNavItem === item.id
                      ? 'bg-white text-neutral-900 font-semibold ring-1 ring-inset ring-neutral-200/70 shadow-[0_1px_0_rgba(16,24,40,0.04),0_4px_12px_-4px_rgba(16,24,40,0.08)]'
                      : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                  }`}
                  title={sidebarCollapsed ? item.name : ''}>
                  {activeNavItem === item.id && !sidebarCollapsed && (
                    <span aria-hidden="true" className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-gradient-to-b from-primary-500 to-primary-700" />
                  )}
                  <span className={`flex-shrink-0 ${activeNavItem === item.id ? 'text-primary-600' : ''}`}>{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span initial={{ opacity: 0, width: 0 }} animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }} transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap">{item.name}</motion.span>
                    )}
                  </AnimatePresence>
                  {!sidebarCollapsed && item.badge && (
                    <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">{item.badge}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Bottom Nav */}
        <div className="border-t border-neutral-200 py-2 px-3">
          <ul className="space-y-1">
            {bottomNavItems.map((item) => (
              <li key={item.id}>
                <button onClick={item.onClick}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-neutral-700 hover:bg-neutral-100 transition-all"
                  title={sidebarCollapsed ? item.name : ''}>
                  <span className="flex-shrink-0">{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span initial={{ opacity: 0, width: 0 }} animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }} transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap">{item.name}</motion.span>
                    )}
                  </AnimatePresence>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Collapse Toggle */}
        <div className="p-4 border-t border-neutral-200">
          <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 transition-colors">
            <svg className="w-5 h-5 transition-transform" style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
              fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="bg-white border-b border-neutral-200 px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            {/* Mobile: Back button + Logo. The desktop sidebar shows Back but mobile
                hides the sidebar, so surface Back in the mobile header. */}
            <div className="lg:hidden flex items-center gap-2">
              {onBack && (
                <button
                  onClick={onBack}
                  aria-label="Back"
                  className="p-2 -ml-2 rounded-lg text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
              )}
              <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
              <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
            </div>
            <div className="hidden lg:block">
              <h1 className="text-xl font-semibold text-neutral-900">{getPageTitle()}</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full">
                <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                <span className="text-xs font-medium hidden sm:inline">Post-Session</span>
              </div>
              <button onClick={() => onBack ? onBack() : navigate('/join')}
                className="px-3 py-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors">Leave</button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 bg-white overflow-y-auto pb-40 lg:pb-6">
          <div className="lg:hidden mb-4"><h1 className="text-lg font-bold text-neutral-900">{getPageTitle()}</h1></div>
          {renderContent()}
          {/* Mobile-only spacer — ensures the last submit button (AI Coach textarea,
              Add Note, etc.) clears the fixed bottom nav regardless of how the
              flex-col parent resolves overflow-y-auto on short devices. */}
          <div className="lg:hidden h-20" aria-hidden="true" />
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 z-40 safe-area-inset-bottom">
        <nav className="flex justify-around items-center h-14 px-0.5">
          {navigationItems.map((item) => (
            <button key={item.id} onClick={item.onClick}
              className={`flex flex-col items-center justify-center gap-0 px-0.5 py-1 flex-1 min-w-0 transition-colors ${
                activeNavItem === item.id ? '' : 'text-neutral-500'
              }`}
              style={activeNavItem === item.id ? { color: '#ff8a00' } : {}}>
              <span className="[&>svg]:w-4 [&>svg]:h-4">{item.icon}</span>
              <span className="text-[8px] font-medium truncate w-full text-center leading-tight mt-0.5">
                {item.id === 'action-plans' ? 'Plans' : item.id === 'ai-coach' ? 'AI Coach' : item.name}
              </span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default PostSessionTab;
